import { inject, Injectable } from '@angular/core';

import { ReplaySubject } from 'rxjs';

import { I18N_CONFIG_TOKEN } from './i18n.tokens';
import type { Translations } from './i18n.tokens';
import type { SupportedTranslations } from './phrase-app/phrase-app-translation.service';

export interface I18nTranslateOptions {
  count?: number;
  defaultValue?: string;
  [key: string]: any;
}

export interface LanguageItem {
  code: string;
  name: string;
  nativeName: string;
  selected: boolean;
}

export abstract class TranslationsProvider {
  public localeChanged = new ReplaySubject<string>(1);
  public abstract hasEditor: boolean;
  public abstract locale: string;
  public abstract defaultLocale: SupportedTranslations;
  public abstract availableLocales: string[];
  public abstract lookup(key: string, locale?: string | null): string | null;
  public abstract setLocale(locale: string): boolean;

  public setTranslations(translations: Translations[]): void {
    // noop
  }

  public markForEditor(key: string): string {
    return '';
  }
}

export const injectTranslate = () => {
  const i18n = inject(I18nService);
  return (key: string | TemplateStringsArray, opts?: I18nTranslateOptions) =>
    i18n.translate(Array.isArray(key) ? key[0] : key, opts);
};

@Injectable()
export class I18nService {
  private readonly translationsProvider = inject(TranslationsProvider);
  private readonly parentService = inject(I18nService, { optional: true, skipSelf: true });
  private readonly config = inject(I18N_CONFIG_TOKEN, { optional: true });

  public readonly localeChanged = this.translationsProvider.localeChanged.asObservable();

  public get locale(): string {
    return this.translationsProvider.locale;
  }

  public set locale(locale: SupportedTranslations) {
    const isLocaleSet = this.translationsProvider.setLocale(locale);
    if (!isLocaleSet) {
      console.error(`Unknown I18nService#locale= argument: ${locale}`);
    }
  }

  public translate(key: string, options: I18nTranslateOptions | any = {}): string {
    let tempKey = key;
    if (this.translationsProvider.hasEditor) {
      return this.translationsProvider.markForEditor(tempKey);
    }
    let translation =
      this.translationsProvider.lookup(tempKey, this.locale) ||
      this.translationsProvider.lookup(tempKey, this.translationsProvider.defaultLocale);

    if (!translation && options.count !== undefined && Number.isInteger(options.count)) {
      tempKey = this.pluralizeKey(tempKey, <{ count: number }>options);
      translation =
        this.translationsProvider.lookup(tempKey, this.locale) ||
        this.translationsProvider.lookup(tempKey, this.translationsProvider.defaultLocale);
    }

    if (!translation && options.defaultValue !== undefined) {
      return options.defaultValue;
    }

    if (!translation) {
      return this.parentService
        ? this.parentService.translate(tempKey, options)
        : `missing translation: ${tempKey}`;
    }

    return this.interpolate(translation, options);
  }

  public pluralizeKey(key: string, options: { count: number }): string {
    if (options.count === 0) {
      return `${key}.zero`;
    } else if (options.count === 1) {
      return `${key}.one`;
    } else {
      return `${key}.other`;
    }
  }

  public interpolate(translation: string, options: I18nTranslateOptions | any = {}): string {
    return Object.keys(options).reduce((t, key) => {
      const search = `%{${key}}`;
      const replace = options[key];
      return t.split(search).join(replace!);
    }, translation);
  }
}
